import { useState } from "react";
import { Link } from "react-router-dom";
import { useProducts } from "@/hooks/useProducts";
import ProductCarousel from "@/components/base/ProductCarousel";

const tabs = ["All", "Clothes", "Shoes", "Bags", "Nails"] as const;
type Tab = (typeof tabs)[number];

export default function FeaturedProducts() {
  const [activeTab, setActiveTab] = useState<Tab>("All");

  const { products: featured, loading } = useProducts({ featured: true });

  const filtered = featured.filter((p) => {
    if (activeTab === "All") return true;
    return p.category === activeTab.toLowerCase();
  });

  return (
    <section
      className="py-20 lg:py-28 px-6 lg:px-16 bg-moonlight-warm"
      style={{ fontFamily: "'Jost', sans-serif" }}
    >
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-5 mb-12 max-w-6xl mx-auto">
        <div>
          <p
            className="text-[10px] tracking-ultra-wide uppercase text-midnight/40 mb-3"
            style={{ letterSpacing: "0.3em" }}
          >
            Curated for You
          </p>
          <h2
            className="text-3xl md:text-4xl font-light italic text-midnight"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            The Glow Selection
          </h2>
        </div>
        <Link
          to="/collections/new-arrivals"
          className="text-xs tracking-widest uppercase text-midnight/60 hover:text-midnight transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
        >
          View All <i className="ri-arrow-right-line" />
        </Link>
      </div>

      {/* Tabs */}
      <div className="max-w-6xl mx-auto mb-10">
        <div className="flex items-center gap-1 bg-pearl/20 rounded-full px-1 py-1 w-fit flex-wrap">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`text-xs tracking-wider px-5 py-2 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap ${
                activeTab === tab
                  ? "bg-midnight text-moonlight"
                  : "text-midnight/60 hover:text-midnight"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Carousel */}
      <div className="max-w-6xl mx-auto px-6">
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-pearl/30 aspect-[3/4] mb-3" />
                <div className="bg-pearl/30 h-4 w-3/4 mb-2" />
                <div className="bg-pearl/30 h-3 w-1/2" />
              </div>
            ))}
          </div>
        ) : filtered.length > 0 ? (
          <ProductCarousel products={filtered} />
        ) : (
          <div className="text-center py-16">
            <p className="text-sm text-midnight/40">No products in this category yet.</p>
          </div>
        )}
      </div>
    </section>
  );
}
